import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import TerminationPopUp from "./TerminationPopUp.js/TerminationPopUp";
import { GoEye } from "react-icons/go";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  TERMINATION_FINAL_EXIT_COLUMNS,
  TERMINATION_FINAL_EXIT_CONFIG,
  TERMINATION_FINAL_EXIT_SEARCHABLE_FIELDS,
  renderVisaProvider,
  renderTerminationStatus
} from './TerminationFinalExitConstants';

const TerminationFinalExitList = ({
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Popup state management
  const [popupState, setPopupState] = useState(() => ({
    termination: false,
    propData: null
  }));

  // Handle eye icon click to open popup
  const handleViewClick = useCallback((item) => {
    setPopupState({
      termination: true,
      propData: item
    });
  }, []);

  // Close popup handler
  const handleClosePopup = useCallback(() => {
    setPopupState({
      termination: false,
      propData: null
    });
  }, []);

  // Custom cell renderer for special cases
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return renderVisaProvider(item);
      
      case 'total_salary':
        return item?.total_salary?.toString() || "-";
      
      case 'idle_aging':
        return item?.idle_aging?.toString() || "-";
      
      case 'termination_status':
        return renderTerminationStatus(item);
      
      case 'action':
        return checkAccess("DEMOBILIZATION", "final-exit-edit") ? (
          <div
            className="ml-2 cursor-pointer"
            onClick={() => handleViewClick(item)}
          >
            <GoEye fontSize={22} className="ml-2" />
          </div>
        ) : null;
      
      default:
        return null;
    }
  }, [handleViewClick, dashboardData]);

  // Download handlers
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: `Excel Downloaded (${result.recordCount} records)`,
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
      color: "error"
    }));
  }, [dispatch]);

  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={TERMINATION_FINAL_EXIT_CONFIG.API_ENDPOINT}
        downloadEndpoint={TERMINATION_FINAL_EXIT_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={TERMINATION_FINAL_EXIT_COLUMNS}
        searchableFields={TERMINATION_FINAL_EXIT_SEARCHABLE_FIELDS}
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={TERMINATION_FINAL_EXIT_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={TERMINATION_FINAL_EXIT_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={TERMINATION_FINAL_EXIT_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        enableSearch={true}
        searchPlaceholder={TERMINATION_FINAL_EXIT_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={TERMINATION_FINAL_EXIT_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={TERMINATION_FINAL_EXIT_CONFIG.NO_DATA_MESSAGE}
        className="termination-final-exit-table"
      />

      {/* Popup rendering */}
      {popupState.termination && (
        <TerminationPopUp
          PopUp={popupState.termination}
          setPopUp={handleClosePopup}
          setTrigger={setTrigger}
          trigger={trigger}
          propData={popupState.propData}
          endpoint={"termination"}
        />
      )}
    </div>
  );
};

TerminationFinalExitList.displayName = 'TerminationFinalExitList';

export default TerminationFinalExitList;
